"use client";
import { Input, PhoneInput } from "@/components";
import { useCountriesInfinite } from "@/hooks";
import type { CountryData } from "@/types";
import { useMemo, useState } from "react";
import type { CountryCode } from "libphonenumber-js";
import { useFormContext } from "react-hook-form";
import { LuPlus, LuX } from "react-icons/lu";
import { useTranslations } from "next-intl";

interface ReferenceFormProps {
  baseName: string; // e.g., "experiences.0" or "educations.1"
  onRemove?: () => void;
  disabled?: boolean;
  missingSet?: Set<string>;
  flash?: boolean; // whether we are currently flashing
  flashClassName?: string; // class to apply when flashing a missing field
}

export const ReferenceForm = ({ baseName, onRemove, disabled = false, missingSet, flash, flashClassName = "flash-border-3x" }: ReferenceFormProps) => {
  const { watch, setValue } = useFormContext();
  const [showForm, setShowForm] = useState(false);

  // Búsqueda server-side por nombre + paginación incremental para el
  // dropdown del `PhoneInput` (catálogo de países).
  const [phoneCountrySearchQuery, setPhoneCountrySearchQuery] = useState("");
  const {
    data: phoneCountriesPages,
    isLoading: isCountriesLoading,
    hasNextPage: hasMorePhoneCountries,
    fetchNextPage: fetchNextPhoneCountriesPage,
    isFetchingNextPage: isFetchingNextPhoneCountriesPage,
  } = useCountriesInfinite(phoneCountrySearchQuery);

  const countries = useMemo<CountryData[]>(
    () =>
      (phoneCountriesPages?.pages?.flatMap((p) => p.data) ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        display: c.name,
        country_code: (c.country_code ?? "") as CountryCode,
        phone_code: c.phone_code ?? "",
      })),
    [phoneCountriesPages],
  );
  const t = useTranslations("jobberProfile.forms.stepThree.reference");

  const referenceData = watch(`${baseName}.reference`);
  const hasReference = !!referenceData;

  const handleAddReference = () => {
    setValue(`${baseName}.reference`, {
      first_name: "",
      last_name: "",
      relationship: "",
      email: "",
      phone_country: { id: "", name: "" },
      phone_number: "",
    });
    setShowForm(true);
  };

  const handleRemoveReference = () => {
    setValue(`${baseName}.reference`, undefined);
    setShowForm(false);
    if (onRemove) {
      onRemove();
    }
  };

  if (!hasReference && !showForm) {
    return (
      <div className="flex justify-center mt-4 mx-4 mb-8">
        <button
          type="button"
          onClick={handleAddReference}
          disabled={disabled}
          className={`flex items-center gap-2 px-6 py-2 tracking-wide transition-colors duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-full md:border-2 ${
            disabled
              ? "md:border-gray-300 bg-gray-100 text-gray-500 cursor-default"
              : "md:border-brand-500 bg-transparent text-brand-500 hover:bg-brand-500 hover:text-white focus:bg-brand-500 focus:text-white focus:ring-brand-300 cursor-pointer"
          }`}
        >
          <LuPlus size={16} />
          {t("addReference")}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-normal text-base sm:text-sm flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7.00008 6.60156C8.47284 6.60156 9.66675 5.40766 9.66675 3.93489C9.66675 2.46213 8.47284 1.26823 7.00008 1.26823C5.52732 1.26823 4.33341 2.46213 4.33341 3.93489C4.33341 5.40766 5.52732 6.60156 7.00008 6.60156ZM7.00008 6.60156C4.60579 6.60156 2.66675 8.5406 2.66675 10.9349V12.6016H11.3334V10.9349C11.3334 8.5406 9.39437 6.60156 7.00008 6.60156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t("referenceInformation")}
          </h3>
          <button
            type="button"
            onClick={handleRemoveReference}
            disabled={disabled}
            className={`p-1 rounded ${
              disabled
                ? "cursor-default text-gray-400"
                : "hover:bg-red-100 text-red-600"
            }`}
            title={t("removeReference")}
          >
            <LuX size={16} />
          </button>
        </div>
        
        <div className="space-y-4">
          <Input
            name={`${baseName}.reference.first_name`}
            label={t("firstName")}
            placeholder={t("firstNamePlaceholder")}
            required
            disabled={disabled}
            className={`bg-[#F9F9F9] ${missingSet?.has('reference_first_name') && flash ? flashClassName : ''}`}
            noBorder
            noShadow
          />
          <Input
            name={`${baseName}.reference.last_name`}
            label={t("lastName")}
            placeholder={t("lastNamePlaceholder")}
            required
            disabled={disabled}
            className={`bg-[#F9F9F9] ${missingSet?.has('reference_last_name') && flash ? flashClassName : ''}`}
            noBorder
            noShadow
          />
          <Input
            name={`${baseName}.reference.relationship`}
            label={t("relationship")}
            placeholder={t("relationshipPlaceholder")}
            required
            disabled={disabled}
            className={`bg-[#F9F9F9] ${missingSet?.has('reference_relationship') && flash ? flashClassName : ''}`}
            noBorder
            noShadow
          />
          <Input
            name={`${baseName}.reference.email`}
            label={t("email")}
            type="email"
            placeholder={t("emailPlaceholder")}
            required
            disabled={disabled}
            className={`bg-[#F9F9F9] ${missingSet?.has('reference_email') && flash ? flashClassName : ''}`}
            noBorder
            noShadow
          />
          <PhoneInput
            name={`${baseName}.reference`}
            countries={countries}
            label={t("phoneNumber")}
            placeholder={t("phoneNumberPlaceholder")}
            required={false}
            isLoading={isCountriesLoading}
            onSearchChange={setPhoneCountrySearchQuery}
            hasNextPage={hasMorePhoneCountries}
            fetchNextPage={fetchNextPhoneCountriesPage}
            isFetchingNextPage={isFetchingNextPhoneCountriesPage}
            disabled={disabled}
            noBorder
            noShadow
            className="bg-[#F9F9F9]"
          />
        </div>
      </div>
    </div>
  );
};
